import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Trophy } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { useAggregateLeaderboard } from '@/api/query/useLeaderboard';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useUser } from '@/hooks/useUser';
import { useAuth } from '@/contexts/AuthContext';
import { UserPickemsList } from './UserPickemsList';
import { WeeklyBreakdownList } from './WeeklyBreakdownList';
import LeaderboardRowSkeleton from './LeaderboardRowSkeleton';
import { CountUpNumber } from '@/components/shared/CountUpNumber';

interface LeaderboardSectionProps {
  title?: string;
  className?: string;
}

const LeaderboardSection: React.FC<LeaderboardSectionProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { iframeParams } = useAuth();
  const prefersReducedMotion = useReducedMotion();

  // Calculate weeks data including the latest week offset
  const { weeks, latestWeekOffset } = useMemo(() => {
    // Season start date (Week 1 Monday) - Oct 13, 2025
    // TODO: Move to backend configuration or environment variable
    // This date represents the official season start and is used to calculate current week number
    const SEASON_START_DATE = new Date('2025-10-13T00:00:00Z');
    const today = new Date();
    const daysSinceReference = Math.floor(
      (today.getTime() - SEASON_START_DATE.getTime()) / (1000 * 60 * 60 * 24)
    );
    const currentWeekNumber = Math.floor(daysSinceReference / 7) + 1;

    // Only show weeks from Week 1 up to current week (hide future weeks)
    // Week 1 = offset 0 (first week starting from SEASON_START_DATE)
    // Week 2 = offset 1 (second week), etc.
    const weeksArray = Array.from({ length: Math.max(1, currentWeekNumber) }, (_, i) => ({
      label: `Week ${i + 1}`,
      offset: i, // 0 = Week 1, 1 = Week 2, etc.
    })).reverse();

    // Latest week offset is the highest index (currentWeekNumber - 1)
    const latestOffset = Math.max(0, currentWeekNumber - 1);

    return { weeks: weeksArray, latestWeekOffset: latestOffset };
  }, []);

  // Daily selector: the last 7 calendar days (offset 0 = today).
  const days = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - i);
      return { offset: i, label: i === 0 ? t('leaderboard.today') : fmt.format(d) };
    });
  }, [t]);

  // Monthly selector: the last 6 calendar months (offset 0 = this month).
  const months = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' });
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      return { offset: i, label: fmt.format(d) };
    });
  }, []);

  // SkyPicks (full-navigation) leaderboard is grouped Daily / Monthly (DKPLAT-471).
  const [activeTab, setActiveTab] = useState<'today' | 'week' | 'month' | 'season'>('today');
  // Generic "periods back" offset shared by the Daily (day), Week and Monthly
  // (month) selectors. 0 = most recent: today / this week / this month.
  const [selectedOffset, setSelectedOffset] = useState(0);

  // Map activeTab to period for API. Daily → today, Monthly → month (last 30 days).
  const period = useMemo(() => {
    if (activeTab === 'season') return 'year';
    if (activeTab === 'month') return 'month';
    if (activeTab === 'week') return 'week';
    return 'today';
  }, [activeTab]);

  // Get user's league code from iframe params location
  const leagueCode = iframeParams.location;

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAggregateLeaderboard(period, selectedOffset, leagueCode);

  const { leaderboard, currentUserEntry } = useMemo(() => {
    if (!data?.pages) return { leaderboard: [], currentUserEntry: null };

    // Flatten all pages into a single leaderboard array
    const allEntries = data.pages.flatMap(page => page.leaderboard);

    if (allEntries.length === 0) return { leaderboard: [], currentUserEntry: null };

    // Find current user in leaderboard
    const userEntry = currentUser ? allEntries.find(e => e.user_id === currentUser.id) : null;

    // Create leaderboard entries
    const entries = allEntries.map(entry => ({
      rank: entry.rank,
      user_id: entry.user_id,
      username: entry.username,
      points: entry.total_points,
      isCurrentUser: currentUser?.id === entry.user_id,
      isWinner: entry.rank === 1,
      pickems_data: entry.pickems_data,
    }));

    return {
      leaderboard: entries,
      currentUserEntry: userEntry
        ? {
            rank: userEntry.rank,
            user_id: userEntry.user_id,
            username: userEntry.username,
            points: userEntry.total_points,
            isCurrentUser: true,
            isWinner: userEntry.rank === 1,
            pickems_data: userEntry.pickems_data,
          }
        : null,
    };
  }, [data, currentUser]);

  const tabs = [
    { id: 'today' as const, label: t('leaderboard.daily') },
    { id: 'month' as const, label: t('leaderboard.monthly') },
  ];

  // Infinite scroll implementation
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Reset to the most recent period when switching tabs: latest week for the
  // legacy week tab, otherwise offset 0 (today / this month).
  const handleTabChange = (tabId: 'today' | 'week' | 'month' | 'season') => {
    setActiveTab(tabId);
    setSelectedOffset(tabId === 'week' ? latestWeekOffset : 0);
  };

  return (
    <div data-test-id="leaderboard-section" className={`${className}`}>
      {/* Tab Navigation */}
      <div className="mb-4">
        <div className="flex space-x-1 bg-app-surface rounded-xl p-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-test-id={`leaderboard-period-${tab.id}`}
              onClick={() => handleTabChange(tab.id)}
              className={`
                flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-smooth
                ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'bg-black/20 text-text-secondary hover:text-text-primary'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Period Selection — day buttons under Daily, month buttons under Monthly
          (the legacy week tab reuses the same row). */}
      {activeTab !== 'season' && (
        <div className="mb-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {(activeTab === 'today' ? days : activeTab === 'month' ? months : weeks).map(item => (
              <button
                key={item.offset}
                data-test-id={`leaderboard-offset-${activeTab}-${item.offset}`}
                onClick={() => setSelectedOffset(item.offset)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-smooth
                  ${
                    selectedOffset === item.offset
                      ? 'bg-white text-gray-900'
                      : 'bg-black/20 text-text-secondary hover:text-text-primary'
                  }
                `}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard Header */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-text-secondary text-sm font-medium px-2">
          <div className="flex items-center gap-4">
            <span>{t('leaderboard.rank')}</span>
            <span>{t('leaderboard.user')}</span>
          </div>
          <span>{t('leaderboard.points')}</span>
        </div>
      </div>

      {/* Leaderboard Content */}
      <div>
        {isLoading && (
          <div className="space-y-2">
            {[...Array(15)].map((_, index) => (
              <motion.div
                key={`skeleton-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.3 }}
              >
                <LeaderboardRowSkeleton />
              </motion.div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-center text-red-500 py-8">{t('leaderboard.failedToLoad')}</div>
        )}

        {!isLoading && !error && leaderboard.length === 0 && (
          <div className="text-center text-text-secondary py-8">{t('leaderboard.noData')}</div>
        )}

        {!isLoading && !error && leaderboard.length > 0 && (
          <>
            {/* Current User Entry (Always at top) */}
            {currentUserEntry && (
              <div className="mb-4">
                <div
                  data-test-id={`leaderboard-row-${currentUserEntry.user_id}`}
                  data-test-rank={currentUserEntry.rank}
                  data-test-is-current-user="true"
                  className="rounded-xl overflow-hidden bg-pickem-primary text-white"
                >
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-4">
                        <span className="font-bold">{currentUserEntry.rank}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{currentUserEntry.username}</span>
                          {currentUserEntry.isWinner && (
                            <Trophy
                              data-test-id="trophy-icon"
                              className="w-4 h-4 text-yellow-400"
                            />
                          )}
                        </div>
                      </div>
                      <CountUpNumber
                        data-test-id="points-display"
                        className="font-bold"
                        value={currentUserEntry.points}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Leaderboard List */}
            <Accordion type="single" collapsible className="space-y-2">
              <AnimatePresence mode="sync">
                {leaderboard
                  .filter(player => !player.isCurrentUser)
                  .map((player, index) => (
                    <motion.div
                      key={player.user_id}
                      // `layout="position"` so a row that changes rank slides to its new
                      // place instead of the list repainting in a new order. Position
                      // only — a row also changes HEIGHT when its picks accordion opens,
                      // and animating that fights the accordion's own transition.
                      layout={prefersReducedMotion ? false : 'position'}
                      // Tied to the rank so a row measures only when its position in the
                      // table actually changed, not on every re-render of the list.
                      layoutDependency={player.rank}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      // The layout transition is spelled out separately: left to inherit
                      // the entrance transition it would pick up that row's stagger delay
                      // too, so on a score update the lower rows would sit still for up to
                      // 300ms — overlapping each other — before sliding.
                      transition={{
                        delay: Math.min(index * 0.01, 0.3),
                        duration: 0.3,
                        layout: { delay: 0, duration: 0.3, ease: 'easeOut' },
                      }}
                    >
                      <AccordionItem
                        value={`player-${player.user_id}`}
                        data-test-id={`leaderboard-row-${player.user_id}`}
                        data-test-rank={player.rank}
                        data-test-is-current-user={player.isCurrentUser}
                        className={`
                        rounded-xl border-0 overflow-hidden
                        ${player.isCurrentUser ? 'bg-pickem-primary text-white' : 'bg-game-card'}
                      `}
                      >
                        <AccordionTrigger
                          className={`
                      px-4 py-3 hover:no-underline
                      ${
                        player.isCurrentUser
                          ? 'text-white hover:bg-pickem-primary-dark'
                          : 'text-text-primary hover:bg-game-card-active'
                      }
                    `}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-4">
                              <span className="font-bold">{player.rank}</span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{player.username}</span>
                                {player.isWinner && (
                                  <Trophy
                                    data-test-id="trophy-icon"
                                    className="w-4 h-4 text-yellow-400"
                                  />
                                )}
                              </div>
                            </div>
                            <CountUpNumber
                              data-test-id="points-display"
                              className="font-bold"
                              value={player.points}
                            />
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div
                            className={`px-4 ${player.isCurrentUser ? 'text-white' : 'text-text-secondary'}`}
                          >
                            <div className="text-sm">
                              {/* DKPLAT-618: the column header now lives inside the
                                  breakdown lists so it can hide Cash/Crowns based
                                  on each list's `visibleCurrencies`. */}
                              {activeTab === 'season' ? (
                                <WeeklyBreakdownList
                                  userId={player.user_id}
                                  leagueCode={leagueCode}
                                  isCurrentUser={player.isCurrentUser}
                                />
                              ) : (
                                <UserPickemsList
                                  userId={player.user_id}
                                  period={period}
                                  weekOffset={selectedOffset}
                                  leagueCode={leagueCode}
                                  isCurrentUser={player.isCurrentUser}
                                />
                              )}
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </motion.div>
                  ))}
              </AnimatePresence>
            </Accordion>

            {/* Infinite scroll sentinel */}
            {hasNextPage && (
              <div ref={loadMoreRef} className="py-2 text-center">
                {isFetchingNextPage ? (
                  <LoadingSpinner />
                ) : (
                  <div className="text-text-secondary text-sm">
                    {t('leaderboard.scrollForMore')}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardSection;
